<script>
  import { createEventDispatcher, onDestroy } from 'svelte';
  import { fade, scale } from 'svelte/transition';
  import { portal } from '../../lib/portal.js';

  /** @type {'default' | 'warning'} */
  export let variant = 'default';
  export let show = false;
  export let title = 'Confirmar';
  export let message = '¿Está seguro?';
  export let confirmLabel = 'Confirmar';
  export let cancelLabel = 'Cancelar';
  /** @type {{ sku?: string, descrip?: string, requested: number, picked: number, note?: string }[]} */
  export let discrepancies = [];

  const dispatch = createEventDispatcher();

  function handleConfirm() {
    dispatch('confirm');
  }

  function handleCancel() {
    dispatch('cancel');
  }

  function handleBackdropClick(event) {
    if (event.target === event.currentTarget) {
      handleCancel();
    }
  }

  function handleKeydown(event) {
    if (event.key === 'Escape') {
      handleCancel();
    }
  }

  $: if (show && typeof document !== 'undefined') {
    document.body.style.overflow = 'hidden';
  } else if (typeof document !== 'undefined') {
    document.body.style.overflow = '';
  }

  onDestroy(() => {
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
  });
</script>

<svelte:window on:keydown={show ? handleKeydown : undefined} />

{#if show}
  <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
  <div
    use:portal
    class="confirm-overlay"
    transition:fade={{ duration: 180 }}
    on:click={handleBackdropClick}
    role="presentation"
  >
    <div
      class="confirm-dialog"
      class:confirm-dialog--warning={variant === 'warning'}
      transition:scale={{ duration: 180, start: 0.96 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      on:click|stopPropagation
    >
      <div class="confirm-header">
        {#if variant === 'warning'}
          <div class="confirm-icon confirm-icon--warning" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
        {:else}
          <div class="confirm-icon confirm-icon--default" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        {/if}
        <div>
          <h2 id="confirm-dialog-title" class="confirm-title">{title}</h2>
          <p class="confirm-message">{message}</p>
        </div>
      </div>

      {#if discrepancies.length}
        <div class="confirm-discrepancies">
          <p class="confirm-discrepancies__label">
            {discrepancies.length} línea{discrepancies.length === 1 ? '' : 's'} con diferencia
          </p>
          <div class="confirm-table-wrap">
            <table class="confirm-table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Producto</th>
                  <th class="num">Sol.</th>
                  <th class="num">Pick</th>
                  <th class="num">Diff</th>
                </tr>
              </thead>
              <tbody>
                {#each discrepancies as row (row.sku + row.descrip)}
                  <tr>
                    <td class="sku">{row.sku || '—'}</td>
                    <td class="desc">{row.descrip || '—'}</td>
                    <td class="num">{row.requested}</td>
                    <td class="num">{row.picked}</td>
                    <td class="num diff">{row.note || '—'}</td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        </div>
      {/if}

      <slot />

      <div class="confirm-actions">
        <button type="button" class="confirm-btn confirm-btn--cancel" on:click={handleCancel}>
          {cancelLabel}
        </button>
        <button
          type="button"
          class="confirm-btn confirm-btn--confirm"
          class:confirm-btn--warning={variant === 'warning'}
          on:click={handleConfirm}
        >
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .confirm-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    background: rgba(15, 23, 42, 0.55);
    backdrop-filter: blur(3px);
  }

  .confirm-dialog {
    width: 100%;
    max-width: 32rem;
    max-height: min(90vh, 640px);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 0.75rem;
    box-shadow:
      0 25px 50px -12px rgba(0, 0, 0, 0.28),
      0 0 0 1px rgba(0, 0, 0, 0.04);
    outline: none;
  }

  .confirm-dialog--warning {
    box-shadow:
      0 25px 50px -12px rgba(0, 0, 0, 0.28),
      0 0 0 1px rgba(245, 158, 11, 0.25);
  }

  .confirm-header {
    display: flex;
    gap: 0.875rem;
    padding: 1.25rem 1.25rem 0;
  }

  .confirm-icon {
    flex-shrink: 0;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 9999px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .confirm-icon svg {
    width: 1.35rem;
    height: 1.35rem;
  }

  .confirm-icon--default {
    background: #ccfbf1;
    color: #0f766e;
  }

  .confirm-icon--warning {
    background: #fef3c7;
    color: #b45309;
  }

  .confirm-title {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 700;
    color: #1e293b;
    line-height: 1.3;
  }

  .confirm-message {
    margin: 0.35rem 0 0;
    font-size: 0.875rem;
    color: #64748b;
    line-height: 1.5;
  }

  .confirm-discrepancies {
    margin: 1rem 1.25rem 0;
    padding: 0.75rem;
    background: #fffbeb;
    border-radius: 0.5rem;
    border: 1px solid #fde68a;
  }

  .confirm-discrepancies__label {
    margin: 0 0 0.5rem;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #92400e;
  }

  .confirm-table-wrap {
    max-height: 11rem;
    overflow: auto;
    border-radius: 0.375rem;
    background: #fff;
    border: 1px solid #fde68a;
  }

  .confirm-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.8125rem;
  }

  .confirm-table th {
    position: sticky;
    top: 0;
    background: #fef9c3;
    color: #78350f;
    font-weight: 600;
    text-align: left;
    padding: 0.4rem 0.5rem;
    border-bottom: 1px solid #fde68a;
    white-space: nowrap;
  }

  .confirm-table td {
    padding: 0.45rem 0.5rem;
    border-bottom: 1px solid #fef3c7;
    color: #334155;
    vertical-align: top;
  }

  .confirm-table tr:last-child td {
    border-bottom: none;
  }

  .confirm-table .sku {
    font-family: ui-monospace, monospace;
    font-size: 0.75rem;
    font-weight: 600;
    white-space: nowrap;
  }

  .confirm-table .desc {
    max-width: 9rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .confirm-table .num {
    text-align: right;
    white-space: nowrap;
  }

  .confirm-table .diff {
    font-weight: 600;
    color: #b45309;
  }

  .confirm-actions {
    display: flex;
    gap: 0.625rem;
    justify-content: flex-end;
    padding: 1rem 1.25rem 1.25rem;
    margin-top: auto;
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
  }

  .confirm-btn {
    padding: 0.55rem 1rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    font-weight: 600;
    border: none;
    cursor: pointer;
    transition: background 0.15s ease;
  }

  .confirm-btn--cancel {
    background: #e2e8f0;
    color: #475569;
  }

  .confirm-btn--cancel:hover {
    background: #cbd5e1;
  }

  .confirm-btn--confirm {
    background: #14b8a6;
    color: #fff;
  }

  .confirm-btn--confirm:hover {
    background: #0d9488;
  }

  .confirm-btn--warning {
    background: #d97706;
  }

  .confirm-btn--warning:hover {
    background: #b45309;
  }
</style>
